import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue } from "framer-motion";
import { Flame, BookOpen, Skull } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const Line = ({ children, delay = 0, unlocked, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={unlocked ? { y: 0 } : { y: "115%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = ["EST. 2018", "100% UNCENSORED", "PHYSICAL + DIGITAL", "5-SKULL FAN RATED"];

const HALLOWEEN_COVER = {
  src: "/covers/human-hunters-1-halloween.webp",
  alt: "Human Hunters #1 Halloween Special cover",
};

const BASE_COVERS = [
  {
    src: "/covers/ninja-bimbos-3.jpg",
    alt: "Ninja Bimbos #3 cover",
  },
  {
    src: "/covers/dark-america.jpg",
    alt: "Dark America cover",
  },
  {
    src: "/covers/mary-issue-3.png",
    alt: "Mary Machinegun Issue 3 cover",
  },
  {
    src: "/covers/mary-issue-4.png",
    alt: "Mary Machinegun Issue 4 cover",
  },
  {
    src: "/covers/human-hunters-4.jpg",
    alt: "Human Hunters #4 cover",
  },
];

const isOctober = new Date().getMonth() === 9;
const ROTATING_COVERS = isOctober ? [HALLOWEEN_COVER, ...BASE_COVERS] : [...BASE_COVERS, HALLOWEEN_COVER];

export default function Hero({ unlocked }) {
  const ref = useRef(null);
  const [coverIdx, setCoverIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yFront = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useTransform(my, [-0.5, 0.5], [6, -6]);
  const ry = useTransform(mx, [-0.5, 0.5], [-8, 8]);

  useEffect(() => {
    const t = setInterval(() => setCoverIdx((i) => (i + 1) % ROTATING_COVERS.length), 4500);
    return () => clearInterval(t);
  }, []);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden newsprint-grain pt-24 pb-16">
      <div className="absolute inset-0 halftone opacity-10 animate-flicker pointer-events-none" />
      <div className="absolute inset-0 blood-vignette pointer-events-none" />
      <div className="absolute -left-24 top-1/3 w-96 h-96 rounded-full bg-zinepurple/25 blur-[120px] pointer-events-none" />
      <div className="absolute -right-24 bottom-0 w-[28rem] h-[28rem] rounded-full bg-blooddeep/30 blur-[130px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-2 gap-16 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, rotate: -6, scale: 0.8 }}
            animate={unlocked ? { opacity: 1, rotate: -3, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-block bg-blooddeep text-white font-marker text-sm sm:text-base px-4 py-1.5 mb-8 ink-shadow-sm"
            data-testid="hero-badge"
          >
            GOTHIC HORROR COMIX — YOUR MAMMA WARNED YOU ABOUT HER
          </motion.div>

          <h1 data-testid="hero-title" className="font-display leading-[0.95] tracking-tight">
            <Line unlocked={unlocked} delay={0.25} className="text-5xl sm:text-7xl lg:text-8xl text-paper">
              ATROCITY
            </Line>
            <Line unlocked={unlocked} delay={0.4} className="text-5xl sm:text-7xl lg:text-8xl text-crimson">
              COMIX
            </Line>
            <Line unlocked={unlocked} delay={0.55} className="text-2xl sm:text-4xl lg:text-5xl mt-3">
              <span className="text-crimson">SINISTER.</span> <span className="text-paper">SEXY.</span>{" "}
              <span className="text-crimson">SAVAGE.</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={unlocked ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="font-type text-sm sm:text-base text-paper/70 max-w-md mt-8 leading-relaxed"
          >
            Horror stories with teeth — and heels. Gothic tales of blood, vengeance, and the
            bad girls who always get the last word. Uncensored since 2018.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={unlocked ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <a
              data-testid="hero-shop-cta"
              href="/shop"
              className="flex items-center gap-2 font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-7 py-4 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1"
            >
              <Flame size={18} /> Browse the Shop
            </a>
            <button
              data-testid="hero-manifesto-cta"
              onClick={() => scrollToId("#story")}
              className="flex items-center gap-2 font-impact uppercase tracking-wider bg-transparent text-paper border-2 border-paper/50 px-7 py-4 transition-colors duration-150 hover:border-bloodglow hover:text-bloodglow"
            >
              <BookOpen size={18} /> Read the Manifesto
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={unlocked ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="flex flex-wrap gap-x-6 gap-y-2 mt-12"
          >
            {STATS.map((s) => (
              <span key={s} className="flex items-center gap-1.5 font-courier text-xs uppercase tracking-widest text-paper/50">
                <Skull size={11} className="text-crimson" /> {s}
              </span>
            ))}
          </motion.div>
        </div>

        <div
          className="relative h-[420px] sm:h-[540px] hidden md:block"
          style={{ perspective: 1200 }}
          onMouseMove={onMouseMove}
          onMouseLeave={() => { mx.set(0); my.set(0); }}
        >
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="absolute inset-0">
            <motion.div
              style={{ y: yBack, transform: "translateZ(-60px)" }}
              initial={{ opacity: 0, x: 60, rotate: 10 }}
              animate={unlocked ? { opacity: 1, x: 0, rotate: 7 } : {}}
              transition={{ duration: 1, delay: 0.5 }}
              className="group absolute right-2 top-4 w-52 sm:w-64 aspect-[2/3] spotlight-frame cursor-zoom-in"
            >
              <AnimatePresence>
                <motion.img
                  key={coverIdx}
                  data-testid="hero-rotating-cover"
                  src={ROTATING_COVERS[coverIdx].src}
                  alt={ROTATING_COVERS[coverIdx].alt}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-125"
                />
              </AnimatePresence>
            </motion.div>
            <motion.div
              style={{ y: yFront, transform: "translateZ(60px)" }}
              initial={{ opacity: 0, x: -60, rotate: -12 }}
              animate={unlocked ? { opacity: 1, x: 0, rotate: -6 } : {}}
              transition={{ duration: 1, delay: 0.35 }}
              className="group absolute left-2 sm:left-10 bottom-2 w-60 sm:w-80 spotlight-frame comic-shadow cursor-zoom-in"
            >
              <img
                src="/covers/mary-machinegun-orange.webp"
                alt="Mary Machinegun cover art"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-125"
              />
              <span className="absolute top-3 -left-2 bg-blooddeep text-bone font-marker text-xs px-3 py-1 rotate-[-4deg] ink-shadow-sm">
                FAN BESTSELLER
              </span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={unlocked ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.9, type: "spring" }}
              className="absolute right-6 bottom-10 bg-blooddeep text-white font-marker text-sm px-4 py-2 rotate-6 ink-shadow-sm"
            >
              FRESH FROM THE CRYPT
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
